import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const BranchContext = createContext();

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

export const BranchProvider = ({ children }) => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch branches when user is available
  useEffect(() => {
    if (user && user.role !== 'SUPER_ADMIN' && user.role !== 'DISTRIBUTOR') {
      fetchBranches();
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [user]);

  // Set default branch when branches are loaded
  useEffect(() => {
    if (branches.length > 0 && user && !selectedBranch) {
      // Try to find user's branch or use the first one
      const userBranch = branches.find(branch => branch.id === user.branchId);
      setSelectedBranch(userBranch || branches[0]);
    }
  }, [branches, user, selectedBranch]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/branches');
      
      if (response.data.success) {
        setBranches(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchBranch = (branch) => {
    setSelectedBranch(branch);
    // Store selected branch in localStorage for persistence
    localStorage.setItem('selectedBranchId', branch.id);
  };

  // Restore selected branch from localStorage
  useEffect(() => {
    const savedBranchId = localStorage.getItem('selectedBranchId');
    if (savedBranchId && branches.length > 0) {
      const savedBranch = branches.find(branch => branch.id === savedBranchId);
      if (savedBranch) {
        setSelectedBranch(savedBranch);
      }
    }
  }, [branches]);

  const value = {
    branches,
    selectedBranch,
    loading,
    switchBranch,
    fetchBranches
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

